import { useState, useMemo } from "react";
import { Stack, TextField, Button } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import { NearbyWorkerSearchResult } from "../Autocomplete/useGetNearbyWorkers";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { useWorkplaceIds } from "@src/appV2/api/useWorkplaceIds";
import { trim } from "lodash";

interface InviteRequiredConfirmationProps {
  selectedWorker: NearbyWorkerSearchResult;
  onInviteSomeoneElse: () => void;
  onProceed: (confirmed: boolean) => void;
}

export function InviteRequiredConfirmation({
  selectedWorker,
  onInviteSomeoneElse,
  onProceed,
}: InviteRequiredConfirmationProps) {
  const { workerId, fullName: workerName } = selectedWorker;

  const [confirmationText, setConfirmationText] = useState("");
  const { workplaceId, workplaceUserId } = useWorkplaceIds();

  // Log event when invite required professional is requested
  useMemo(() => {
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "INVITE_REQUIRED_REQUESTED_CONFIRMATION",
      workerId,
      workerName,
      workplaceId,
      workplaceUserId,
    });
  }, [workerId, workerName, workplaceId, workplaceUserId]);

  // Check if user confirmation is valid
  const isConfirmationValid = trim(confirmationText, " !.").trim().toLowerCase() === "i understand";

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Text bold variant="body2">
        {workerName} can only receive shift invites because they've received multiple
        professionalism or reliability-related complaints from other workplaces.
      </Text>
      <Text variant="body2">Consider inviting a different professional.</Text>
      <Text variant="body2" color="red">
        To continue, enter "I understand" below.
      </Text>
      <TextField
        fullWidth
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        placeholder="Type 'I understand' here"
        size="small"
      />
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button onClick={onInviteSomeoneElse} variant="outlined">
          Invite Someone Else
        </Button>
        <Button
          onClick={() => {
            if (isConfirmationValid) {
              logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
                action: "INVITE_REQUIRED_CONFIRMATION_ACCEPTED",
                workerId,
                workerName,
                workplaceId,
                workplaceUserId,
              });
            }
            onProceed(isConfirmationValid);
          }}
          variant="contained"
          endIcon={<ArrowForwardIosOutlinedIcon />}
          disabled={!isConfirmationValid}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
}
